import { Badge, Box, Group, Paper, SimpleGrid, Stack, Text, Title } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { CLINICAL_RISK_COLOR } from "@constants/resultColors";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EyeType, TestType } from "../../../types/testTypes";
import {
  getEyeDataForPupillometry,
  getReferenceLinesForPupillometryDataset,
} from "../../new_test_ui/new_test_steps/test-configuration/extra-tests/pupillometry/PupillometryCalculations";
import PupillometryChart from "../../testContainer/common/PupillometryChart";
import {
  ClinicalSummaryMetric,
  ClinicalRiskLevel,
} from "../../testContainer/result/utils/clinicalSummaryTypes";
import { interpretPupillometrySummary } from "../../testContainer/result/utils/pupillometryInterpretation";

interface SimplifiedPupillometryCardProps {
  data: {
    status: string;
    chartData?: any[];
    testType?: TestType;
  };
}

const formatMetricValue = (metric?: ClinicalSummaryMetric) => {
  if (!metric || metric.value === null || metric.value === undefined || metric.value === "") {
    return "—";
  }

  if (typeof metric.value === "string") {
    return metric.value;
  }

  const formattedValue =
    typeof metric.decimals === "number" ? metric.value.toFixed(metric.decimals) : `${metric.value}`;
  return `${formattedValue}${metric.unit ?? ""}`;
};

const getOverviewSummary = (
  riskLevel: ClinicalRiskLevel,
  isRapd: boolean,
  metrics: ClinicalSummaryMetric[],
) => {
  const sizeMetric = metrics.find((metric) => metric.labelKey.endsWith("baselineAnisocoria"));
  const latencyMetric = metrics.find(
    (metric) =>
      metric.labelKey.endsWith("worstLatency") || metric.labelKey.endsWith("latencyDifference"),
  );

  const sizeValue = formatMetricValue(sizeMetric);
  const latencyValue = formatMetricValue(latencyMetric);

  if (isRapd) {
    switch (riskLevel) {
      case "high":
        return {
          headline: "One eye reacted noticeably less strongly to light on this test.",
          detail: `The left-right difference was ${sizeValue}, with response timing difference of ${latencyValue}.`,
        };
      case "monitor":
        return {
          headline: "One eye reacted a little less strongly to light on this test.",
          detail: `The left-right difference was ${sizeValue}, with timing difference of ${latencyValue}.`,
        };
      default:
        return {
          headline: "Both eyes reacted similarly to light on this test.",
          detail: `The left-right difference was ${sizeValue}, with timing difference of ${latencyValue}.`,
        };
    }
  }

  switch (riskLevel) {
    case "high":
      return {
        headline: "The pupil reaction looked clearly uneven or reduced on this test.",
        detail: `The left-right size difference was ${sizeValue} and the slowest response was ${latencyValue}.`,
      };
    case "monitor":
      return {
        headline: "The pupil reaction was a little uneven or slower on this test.",
        detail: `The left-right size difference was ${sizeValue} and the slowest response was ${latencyValue}.`,
      };
    default:
      return {
        headline: "Both pupils reacted similarly on this test.",
        detail: `The left-right size difference was ${sizeValue} and the slowest response was ${latencyValue}.`,
      };
  }
};

export const SimplifiedPupillometryCard = ({ data }: SimplifiedPupillometryCardProps) => {
  const { t } = useTranslation();

  const referenceLines = useMemo(() => {
    if (!data.chartData?.length) return null;
    return getReferenceLinesForPupillometryDataset(data.chartData);
  }, [data.chartData]);

  const leftResults = useMemo(
    () =>
      data.testType === TestType.Pupillometry
        ? getEyeDataForPupillometry(data.chartData ?? [], EyeType.LeftEye, "NORMAL")
        : null,
    [data.chartData, data.testType],
  );
  const rightResults = useMemo(
    () =>
      data.testType === TestType.Pupillometry
        ? getEyeDataForPupillometry(data.chartData ?? [], EyeType.RightEye, "NORMAL")
        : null,
    [data.chartData, data.testType],
  );
  const leftResultsDirect = useMemo(
    () =>
      data.testType === TestType.RAPD
        ? getEyeDataForPupillometry(data.chartData ?? [], EyeType.LeftEye, "DIRECT")
        : null,
    [data.chartData, data.testType],
  );
  const rightResultsDirect = useMemo(
    () =>
      data.testType === TestType.RAPD
        ? getEyeDataForPupillometry(data.chartData ?? [], EyeType.RightEye, "DIRECT")
        : null,
    [data.chartData, data.testType],
  );
  const leftResultsConsensual = useMemo(
    () =>
      data.testType === TestType.RAPD
        ? getEyeDataForPupillometry(data.chartData ?? [], EyeType.LeftEye, "CONSENTUAL")
        : null,
    [data.chartData, data.testType],
  );
  const rightResultsConsensual = useMemo(
    () =>
      data.testType === TestType.RAPD
        ? getEyeDataForPupillometry(data.chartData ?? [], EyeType.RightEye, "CONSENTUAL")
        : null,
    [data.chartData, data.testType],
  );

  const interpretation = useMemo(
    () =>
      interpretPupillometrySummary({
        testType: data.testType,
        leftResults,
        rightResults,
        leftResultsDirect,
        rightResultsDirect,
        leftResultsConsensual,
        rightResultsConsensual,
      }),
    [
      data.testType,
      leftResults,
      leftResultsConsensual,
      leftResultsDirect,
      rightResults,
      rightResultsConsensual,
      rightResultsDirect,
    ],
  );

  const hasData = Boolean(data.chartData?.length && interpretation);

  if (!hasData || !interpretation) {
    return (
      <Paper withBorder p="md" radius="md">
        <Stack align="center" gap="xs" py="xl">
          <IconAlertCircle size={28} color="var(--mantine-color-gray-6)" />
          <Text c="dimmed" size="sm" ta="center">
            No pupillometry test data available
          </Text>
          <Text c="dimmed" size="xs" ta="center">
            Complete a pupillometry test to see your results here
          </Text>
        </Stack>
      </Paper>
    );
  }

  const overviewSummary = getOverviewSummary(
    interpretation.riskLevel,
    data.testType === TestType.RAPD,
    interpretation.metrics,
  );

  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4} style={{ flex: 1 }}>
            <Title order={5}>{t(interpretation.titleKey)}</Title>
            <Text size="sm" c="dimmed">
              {t(interpretation.explainerKey)}
            </Text>
          </Stack>
          <Badge color={CLINICAL_RISK_COLOR[interpretation.riskLevel]} variant="filled" size="lg">
            {t(`extraTests.pupillometry.summary.risk.${interpretation.riskLevel}`)}
          </Badge>
        </Group>

        <Box bg="var(--oculera-surface-alt)" p="sm" style={{ borderRadius: "10px" }}>
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              {overviewSummary.headline}
            </Text>
            <Text size="sm" c="dimmed">
              {overviewSummary.detail}
            </Text>
          </Stack>
        </Box>

        <Box
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: "8px",
            padding: "12px",
          }}
        >
          <Text size="xs" fw={500} mb="xs" c="dimmed">
            {t("extraTests.pupillometry.summary.previewLabel")}
          </Text>
          {referenceLines && (
            <PupillometryChart data={data.chartData} referenceLines={referenceLines} height={220} />
          )}
        </Box>

        <SimpleGrid cols={{ base: 2, sm: 3, lg: 5 }} spacing="lg">
          {interpretation.metrics.map((metric) => (
            <Stack key={metric.labelKey} gap={2}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {t(metric.labelKey)}
              </Text>
              <Text fw={700}>{formatMetricValue(metric)}</Text>
            </Stack>
          ))}
        </SimpleGrid>
      </Stack>
    </Paper>
  );
};
